type MascotMood = 'default' | 'happy' | 'searching' | 'error';

interface CherryMascotProps {
  mood?: MascotMood;
  size?: number;
  className?: string;
}

/**
 * Single cherry mascot character (Hot Cherry-inspired, kawaii style with Zen headband).
 * Used for empty states, error pages, and engagement moments.
 * The cherry-pair CherryLogo is used for branding (header, favicon).
 */
export function CherryMascot({ mood = 'default', size = 48, className }: CherryMascotProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Stem */}
      <path
        d="M32 16C34 8 40 4 46 6"
        stroke="var(--color-success)"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      {/* Leaf */}
      <ellipse
        cx="44" cy="6" rx="5" ry="3"
        fill="var(--color-success)"
        transform="rotate(30 44 6)"
      />
      {/* Body */}
      <circle cx="32" cy="38" r="22" fill="var(--color-cherry-500)" />
      {/* Highlight */}
      <ellipse
        cx="23" cy="28" rx="5" ry="3.5"
        fill="white" opacity="0.3"
        transform="rotate(-20 23 28)"
      />
      {/* Hachimaki headband */}
      <path
        d="M12 31Q32 25 52 31"
        stroke="white" strokeWidth="2.5"
        fill="none" opacity="0.8"
        strokeLinecap="round"
      />
      {/* Headband tails */}
      <line x1="51" y1="31" x2="56" y2="35" stroke="white" strokeWidth="2" opacity="0.8" strokeLinecap="round" />
      <line x1="51" y1="31" x2="57" y2="29" stroke="white" strokeWidth="2" opacity="0.8" strokeLinecap="round" />

      {/* Mood-specific face */}
      {faces[mood]}
    </svg>
  );
}

const faces: Record<MascotMood, React.ReactNode> = {
  default: (
    <>
      <circle cx="24" cy="37" r="4" fill="currentColor" />
      <circle cx="22.5" cy="35.5" r="1.5" fill="white" />
      <circle cx="40" cy="37" r="4" fill="currentColor" />
      <circle cx="38.5" cy="35.5" r="1.5" fill="white" />
      <path d="M27 45Q32 49 37 45" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </>
  ),

  happy: (
    <>
      <path d="M20 37Q24 33 28 37" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M36 37Q40 33 44 37" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M25 44Q32 51 39 44" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </>
  ),

  searching: (
    <>
      <circle cx="25" cy="37" r="4.5" fill="currentColor" />
      <circle cx="26.5" cy="35.5" r="1.5" fill="white" />
      <circle cx="41" cy="37" r="4.5" fill="currentColor" />
      <circle cx="42.5" cy="35.5" r="1.5" fill="white" />
      <ellipse cx="33" cy="46" rx="2.5" ry="3" fill="currentColor" />
    </>
  ),

  error: (
    <>
      <circle cx="24" cy="38" r="4" fill="currentColor" />
      <circle cx="22.5" cy="36.5" r="1.5" fill="white" />
      <circle cx="40" cy="38" r="4" fill="currentColor" />
      <circle cx="38.5" cy="36.5" r="1.5" fill="white" />
      <path d="M20 32L28 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M44 32L36 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M27 48Q32 44 37 48" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <ellipse cx="48" cy="30" rx="2" ry="3" fill="var(--color-info)" opacity="0.6" />
    </>
  ),
};
